import { GraphQLResult, V6Client } from "@aws-amplify/api-graphql";
import {
  AddressInput,
  ContactInfoInput,
  CreateDoctorMutation,
  Doctor,
  HomeOfficeInput,
  ModelDoctorFilterInput,
  QmeLicenseInput,
  ReportSource,
  UpdateDoctorMutation,
} from "~/api/API";
import { createSearchServiceClient } from "~/api/apiClient";
import type { components } from "~/api/generated/search-service-types";
import {
  createDoctorLocationReturningID,
  createDoctorReturningID,
  deleteDoctorLocationReturningID,
  updateDoctorReturningID,
} from "~/api/graphql-custom/mutations";
import {
  findDoctorLocationIdOptimized,
  getDoctorOptimized,
  listDoctorsOptimized,
} from "~/api/graphql-custom/queries";
import { errorReporter, toError } from "~/error-handling";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import {
  PhysicianFormData,
  PhysicianPreferencesFormData,
} from "~/pages/physicians/type";
import type { SearchSelectionMetadata } from "~/services/searchSelection";
import { AuthUserGroup } from "~/types/auth";
import { formatDoctorDisplayName } from "~/utils/doctorName";

const trimOrNull = (s: string | null | undefined): string | null => {
  if (s === null || s === undefined) return null;
  const trimmed = s.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export interface DoctorSearchParams {
  query: string;
  workState?: string;
  homeState?: string;
  specialties?: string[];
  /** Demo users see only demo doctors, non-demo users see only non-demo doctors */
  isDemo?: boolean;
}

/** One doctor hit, from either the AppSync or the search-service search. */
export type DoctorSearchRecord = components["schemas"]["DoctorSearchResult"] &
  SearchSelectionMetadata;

const isUpdateDoctorMutation = (data: unknown): data is UpdateDoctorMutation =>
  typeof data === "object" && data !== null && "updateDoctor" in data;

export class DoctorService {
  private static instance: DoctorService;
  private readonly client: V6Client;

  private constructor(client: V6Client) {
    this.client = client;
  }

  static getInstance(client?: V6Client): DoctorService {
    if (!DoctorService.instance) {
      DoctorService.instance = new DoctorService(
        client || amplifyGraphQLClient,
      );
    }
    return DoctorService.instance;
  }

  async listDoctors(
    filter?: ModelDoctorFilterInput,
    nextToken?: string | null,
    limit = 25,
  ): Promise<{ doctors: Doctor[]; nextToken: string | null }> {
    const response = await this.client.graphql({
      query: listDoctorsOptimized,
      variables: {
        filter,
        nextToken: nextToken || null,
        limit,
      },
    });

    if (response.errors) {
      throw new Error(`Failed to fetch doctors: ${response.errors.toString()}`);
    }

    const items = response.data?.listDoctors?.items || [];
    const newToken = response.data?.listDoctors?.nextToken || null;

    return {
      doctors: items as Doctor[],
      nextToken: newToken,
    };
  }

  async fetchAllDoctors(
    userGroup: AuthUserGroup,
    filter?: ModelDoctorFilterInput,
    isDemo?: boolean,
  ): Promise<Doctor[]> {
    // Build the filter, adding isDemo filter
    const effectiveFilter: ModelDoctorFilterInput = {
      ...filter,
      // Demo users should only see demo doctors, non-demo users should not see demo doctors
      ...(isDemo !== undefined
        ? isDemo
          ? { isDemo: { eq: true } }
          : { isDemo: { ne: true } }
        : {}),
    };

    const fetchPage = async (
      nextToken?: string | null,
      accumulatedDoctors: Doctor[] = [],
    ): Promise<Doctor[]> => {
      const result = await this.listDoctors(effectiveFilter, nextToken);
      const combinedDoctors = [...accumulatedDoctors, ...result.doctors];

      if (result.nextToken) {
        return await fetchPage(result.nextToken, combinedDoctors);
      } else {
        return combinedDoctors;
      }
    };

    // Doctor user should not need to fetch all doctors
    // Our access rule only allows them to fetch their own doctor record anyway
    if (userGroup === AuthUserGroup.DOCTOR) {
      console.warn("Access to doctor list denied");
      return [];
    }

    const allDoctors = await fetchPage();

    return allDoctors.sort((a, b) =>
      formatDoctorDisplayName(a ?? {}).localeCompare(
        formatDoctorDisplayName(b ?? {}),
      ),
    );
  }

  async getDoctor(id: string): Promise<Doctor | null> {
    const response = await this.client.graphql({
      query: getDoctorOptimized,
      variables: { id },
    });

    if (response.errors) {
      throw new Error(`Failed to fetch doctor: ${response.errors.toString()}`);
    }

    return response.data?.getDoctor as Doctor | null;
  }

  /**
   * Search via the search service (POST /search/doctors), which runs it
   * through AsyncSearch and into OpenSearchAudit.
   */
  async searchDoctorsViaApi(
    params: DoctorSearchParams,
  ): Promise<DoctorSearchRecord[]> {
    const { data, error } = await createSearchServiceClient().POST(
      "/search/doctors",
      {
        body: {
          query: params.query,
          work_state: params.workState,
          home_state: params.homeState,
          specialties: params.specialties ?? [],
          is_demo: params.isDemo,
        },
      },
    );
    if (error || !data) throw new Error("Failed to search doctors");
    return data.items.map((item) => ({
      ...item,
      searchAudit: data.audit,
    }));
  }

  async getDoctorLocationId(
    doctorId: string,
    locationId: string,
  ): Promise<string | undefined> {
    const result = await this.client.graphql({
      query: findDoctorLocationIdOptimized,
      variables: {
        doctorId,
        filter: { locationId: { eq: locationId } },
      },
    });

    return result?.data?.doctorLocationsByDoctorId?.items?.[0]?.id;
  }

  /**
   * Links a physician to a location by creating a DoctorLocation relationship.
   * If the relationship already exists, no action is taken.
   * @param doctorId - The unique identifier of the doctor
   * @param locationId - The unique identifier of the location
   */
  async linkPhysicianLocation(doctorId: string, locationId: string) {
    try {
      const doctorLocationId = await this.getDoctorLocationId(
        doctorId,
        locationId,
      );
      if (!doctorLocationId) {
        await this.createDoctorLocation(doctorId, locationId);
      }
    } catch (error) {
      const errObj = toError(error, "Failed to link physician location");
      errorReporter.reportApplicationError(errObj, {
        extra: {
          doctorId,
          locationId,
        },
      });
      throw errObj;
    }
  }

  /**
   * Unlinks a physician from a location by deleting the DoctorLocation relationship.
   * If no relationship exists, no action is taken.
   * @param doctorId - The unique identifier of the doctor
   * @param locationId - The unique identifier of the location
   */
  async unlinkPhysicianLocation(doctorId: string, locationId: string) {
    try {
      const doctorLocationId = await this.getDoctorLocationId(
        doctorId,
        locationId,
      );
      if (doctorLocationId) {
        await this.deleteDoctorLocation(doctorLocationId);
      }
    } catch (error) {
      const errObj = toError(error, "Failed to unlink physician location");
      errorReporter.reportApplicationError(errObj, {
        extra: {
          doctorId,
          locationId,
        },
      });
      throw errObj;
    }
  }

  // Create a DoctorLocation entity
  private async createDoctorLocation(doctorId: string, locationId: string) {
    return await this.client.graphql({
      query: createDoctorLocationReturningID,
      variables: {
        input: {
          doctorId,
          locationId,
        },
      },
    });
  }

  // Delete a DoctorLocation entity
  private async deleteDoctorLocation(doctorLocationId: string) {
    return await this.client.graphql({
      query: deleteDoctorLocationReturningID,
      variables: { input: { id: doctorLocationId } },
    });
  }

  async createPhysician(
    formData: PhysicianFormData,
  ): Promise<GraphQLResult<CreateDoctorMutation>> {
    const c = formData.basicInfo.content;
    const firstName = trimOrNull(c.firstName);
    const lastName = trimOrNull(c.lastName);
    if (!firstName || !lastName) {
      throw new Error(
        "Failed to create physician: first and last name are required",
      );
    }

    const result = (await this.client.graphql({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      query: createDoctorReturningID,
      variables: {
        input: {
          firstName,
          middleName: trimOrNull(c.middleName),
          lastName,
          status: c.status,
          ramped: c.ramped,
          titleExtension: c.titleExtension,
          specialties: c.specialties,
          officePhone: trimOrNull(c.officePhone),
          cellPhone: trimOrNull(c.cellPhone),
          clinicPhone: trimOrNull(c.clinicPhone),
          workCity: trimOrNull(c.workCity),
          workState: c.workState,
          doctorPhysicianPartnerId: c.physicianPartnerId,
          npi: trimOrNull(c.npi),
          contact: {
            email: trimOrNull(c.email),
            address: {
              city: trimOrNull(c.homeCity),
            },
          },
          calendarPreference: {
            mailingList: c.calendarMailingList ?? [],
            showPatientFullName: false,
            showPatientAddress: false,
            showPatientDOB: false,
            showPortalLink: true,
          },
          homeOffice: {
            state: c.homeState,
          },
          licenseNumber: trimOrNull(formData.license.content.caLicenseNumber),
          licenseRenewalDate:
            formData.license.content.caLicenseRenewalDate?.toString(),
          qmeLicense: {
            renewalDate: formData.license.content.qmeLicenseRenewalDate
              ? formData.license.content.qmeLicenseRenewalDate.toString()
              : null,
          },
        },
      },
    })) as GraphQLResult<CreateDoctorMutation>;

    if (result.errors?.length) {
      throw new Error(
        `Failed to create physician: ${result.errors.map((e) => e.message).join(", ")}`,
      );
    }

    return result;
  }

  async updatePhysicianInfo(physician: Doctor, formData: PhysicianFormData) {
    const c = formData.basicInfo.content;

    // Update Doctor
    const updateDoctorPromise = this.client.graphql({
      query: updateDoctorReturningID,
      variables: {
        input: {
          id: physician.id,
          firstName: trimOrNull(c.firstName),
          middleName: trimOrNull(c.middleName),
          lastName: trimOrNull(c.lastName),
          status: c.status,
          ramped: c.ramped,
          titleExtension: c.titleExtension,
          specialties: c.specialties,
          officePhone: trimOrNull(c.officePhone),
          cellPhone: trimOrNull(c.cellPhone),
          clinicPhone: trimOrNull(c.clinicPhone),
          workCity: trimOrNull(c.workCity),
          workState: c.workState,
          doctorPhysicianPartnerId: c.physicianPartnerId,
          npi: trimOrNull(c.npi),
          // AppSync replaces a nested map whole on update, so the fields this
          // form doesn't edit are re-sent from the loaded record (like the
          // calendarPreference flags below). Picked by name: the record carries
          // __typename, which the input types reject. `satisfies Required<...>`
          // keeps each list in sync with the schema: a field added to the input
          // type fails tsc here instead of being silently wiped on save.
          contact: {
            email: trimOrNull(c.email),
            phone: physician.contact?.phone ?? null,
            phoneExtension: physician.contact?.phoneExtension ?? null,
            fax: physician.contact?.fax ?? null,
            homePhoneNumber: physician.contact?.homePhoneNumber ?? null,
            address: {
              street: physician.contact?.address?.street ?? null,
              city: trimOrNull(c.homeCity),
              state: physician.contact?.address?.state ?? null,
              zip: physician.contact?.address?.zip ?? null,
              county: physician.contact?.address?.county ?? null,
            } satisfies Required<AddressInput>,
          } satisfies Required<ContactInfoInput>,
          calendarPreference: {
            mailingList: c.calendarMailingList ?? [],
            // Preserve existing boolean flags — AppSync nested-object updates
            // replace the whole map, so re-send each flag.
            showPatientFullName:
              physician.calendarPreference?.showPatientFullName ?? false,
            showPatientAddress:
              physician.calendarPreference?.showPatientAddress ?? false,
            showPatientDOB:
              physician.calendarPreference?.showPatientDOB ?? false,
            showPortalLink:
              physician.calendarPreference?.showPortalLink ?? false,
          },
          homeOffice: {
            state: c.homeState,
            county: physician.homeOffice?.county ?? null,
          } satisfies Required<HomeOfficeInput>,
          licenseNumber: trimOrNull(formData.license.content.caLicenseNumber),
          licenseRenewalDate:
            formData.license.content.caLicenseRenewalDate?.toString(),
          qmeLicense: {
            licenseNumber: physician.qmeLicense?.licenseNumber ?? null,
            approvalDate: physician.qmeLicense?.approvalDate ?? null,
            renewalDate: formData.license.content.qmeLicenseRenewalDate
              ? formData.license.content.qmeLicenseRenewalDate.toString()
              : null,
          } satisfies Required<QmeLicenseInput>,
          availabilitiesNote: trimOrNull(
            formData.schedulingNote.content.schedulingNote,
          ),
          availabilities: {
            generalAvailabilities:
              formData.generalAvailabilities.content.generalAvailabilities,
            additionalAvailabilities:
              formData.exceptionsAvailability.content.additionalAvailabilities,
            blackoutDates:
              formData.exceptionsAvailability.content.blackoutDates,
          },
          defaultAppointmentDurationMinutes: {
            qme: formData.appointmentDuration.content.qme,
            qmeReevaluation:
              formData.appointmentDuration.content.qmeReevaluation,
            ime: formData.appointmentDuration.content.ime,
            deposition: formData.appointmentDuration.content.deposition,
          },
          defaultEarlyArrivalTimeMinutes: {
            workersComp: formData.earlyArrivalTime.content.workersComp,
            ime: formData.earlyArrivalTime.content.ime,
            deposition: formData.earlyArrivalTime.content.deposition,
          },
        },
      },
    });

    try {
      const doctorResult = await updateDoctorPromise;
      return { doctor: doctorResult };
    } catch (error) {
      const errObj = toError(error, "Failed to update physician info");
      errorReporter.reportApplicationError(errObj, {
        extra: {
          doctorId: physician.id,
        },
      });
      throw errObj;
    }
  }

  async updatePhysicianPreferences(
    physician: Doctor | null,
    formData: PhysicianPreferencesFormData,
  ) {
    const reportPrefs = formData.reportFormatPreferences?.content;
    const evaluationInputPrefs = formData.evaluationInputPreferences?.content;
    const assessmentTreatmentOptions =
      formData.assessmentTreatmentPreferences?.content
        ?.assessmentTreatmentOptions ?? null;
    const preferredAssessmentMode =
      evaluationInputPrefs?.preferredAssessmentMode ?? null;
    // Phase 4: adlPrefs and medicalHistoryPrefs are now managed via FieldTemplate entity,
    // not stored directly on the Doctor record

    const reportPrefsPromise: Promise<
      GraphQLResult<UpdateDoctorMutation>
    > | null =
      (reportPrefs ||
        evaluationInputPrefs ||
        assessmentTreatmentOptions ||
        preferredAssessmentMode !== null) &&
      physician?.id
        ? this.client.graphql({
            query: updateDoctorReturningID,
            variables: {
              input: {
                id: physician.id,
                reportFormatPreferences: {
                  evaluationReport: {
                    sections: reportPrefs.evaluationReport?.sections ?? [],
                    assessmentSubSections:
                      reportPrefs.evaluationReport?.assessmentSubSections ?? [],
                  },
                  reEvaluationReport: {
                    sections: reportPrefs.reEvaluationReport?.sections ?? [],
                    assessmentSubSections:
                      reportPrefs.reEvaluationReport?.assessmentSubSections ??
                      [],
                  },
                  supplementalReport: {
                    sections: reportPrefs.supplementalReport?.sections ?? [],
                    assessmentSubSections:
                      reportPrefs.supplementalReport?.assessmentSubSections ??
                      [],
                  },
                },
                // Phase 4: medicalHistoryFieldIDs and adlFieldSectionMappings removed from Doctor model
                // These are now managed via FieldTemplate entity (see useTemplateApplication hook)
                evaluationInputPreferences: {
                  // history/physicalExamination are still required by the
                  // schema but are always written, so they are pinned to WRITE.
                  evaluationReport: {
                    history: ReportSource.WRITE,
                    physicalExamination: ReportSource.WRITE,
                    assessment:
                      evaluationInputPrefs?.evaluationReport?.assessment ??
                      ReportSource.WRITE,
                  },
                  reEvaluationReport: {
                    history: ReportSource.WRITE,
                    physicalExamination: ReportSource.WRITE,
                    assessment:
                      evaluationInputPrefs?.reEvaluationReport?.assessment ??
                      ReportSource.WRITE,
                  },
                  // Dictate: a supplemental has no per-visit setting, and
                  // WRITE would skip an uploaded dictation (see getAssessmentSource).
                  supplementalReport: {
                    assessment:
                      evaluationInputPrefs?.supplementalReport?.assessment ??
                      ReportSource.DICTATE,
                  },
                },
                preferredAssessmentMode,
                assessmentTreatmentOptions:
                  assessmentTreatmentOptions?.filter(
                    (option): option is string =>
                      option !== null && option !== undefined,
                  ) ?? [],
              },
            },
          })
        : null;

    let reportPrefsResult: GraphQLResult<UpdateDoctorMutation> | null = null;

    try {
      // Run both promises in parallel, but await individually to preserve clear types and order
      const reportRes = await reportPrefsPromise;

      reportPrefsResult = reportRes;

      // Handle physician preferences update errors
      if (reportPrefsResult?.errors) {
        console.error(
          "GraphQL reportFormatPreferences update errors:",
          reportPrefsResult.errors,
        );
        throw new Error("Failed to update reportFormatPreferences");
      }

      return {
        physician: isUpdateDoctorMutation(reportPrefsResult?.data)
          ? reportPrefsResult?.data.updateDoctor
          : null,
      };
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw error;
    }
  }

  async updateDismissedProTips(doctorId: string, dismissedProTips: string[]) {
    try {
      const response = await this.client.graphql({
        query: updateDoctorReturningID,
        variables: {
          input: {
            id: doctorId,
            dismissedProTips,
          },
        },
      });

      if (response.errors) {
        throw new Error(
          `Failed to update dismissed pro tips: ${response.errors.toString()}`,
        );
      }
    } catch (error) {
      const errObj = toError(error, "Failed to update dismissed pro tips");
      errorReporter.reportApplicationError(errObj, {
        tags: {
          component: "DoctorService",
          operation: "update_dismissed_pro_tips",
        },
        extra: {
          doctorId,
          dismissedProTips,
        },
      });
      throw errObj;
    }
  }
}

export const doctorService = DoctorService.getInstance(amplifyGraphQLClient);
